import { useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiAlertTriangle, FiClock, FiLogOut, FiArrowLeft } from 'react-icons/fi';
import { AuthContext } from '../auth/AuthContext';
import api from '../api/axios';

const Unauthorized = () => {
  const navigate = useNavigate();
  const { user, logout, status, syncSessionFromServer, sessionReady } = useContext(AuthContext);
  const userStatus = status || user?.status;

  useEffect(() => {
    if (!sessionReady) return;
    if (userStatus && userStatus !== "approved") {
      navigate("/pending-approval", { replace: true });
    }
  }, [navigate, sessionReady, userStatus]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    api.get("/auth/me")
      .then(({ data }) => syncSessionFromServer(data))
      .catch(() => {});
  }, [syncSessionFromServer]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!sessionReady) {
    return (
      <div style={{ 
        padding: "2rem", 
        textAlign: "center",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f8fafc"
      }}>
        <div>
          <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>⏳</div>
          <div style={{ color: "#64748b" }}>Checking access...</div>
        </div>
      </div>
    );
  }

  // If status is not approved, show pending message
  if (userStatus && userStatus !== "approved") {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2C63AE 0%, #1272B3 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        fontFamily: "'Inter', sans-serif"
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: '3rem 2.5rem',
          width: '100%',
          maxWidth: 500,
          boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
          textAlign: 'center'
        }}>
          <div style={{
            width: 80,
            height: 80,
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <FiClock size={40} color="#b45309" />
          </div>

          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: 800,
            color: '#b45309',
            marginBottom: '0.75rem'
          }}>
            Account Not Approved Yet
          </h1>

          <p style={{
            color: '#64748b',
            fontSize: '1rem',
            lineHeight: 1.6,
            marginBottom: '1.5rem'
          }}>
            Your account is not approved yet by Admin/Manager. Please contact your manager for immediate approval.
          </p>

          {user?.email && (
            <div style={{
              background: '#f8fafc',
              borderRadius: 8,
              padding: '0.75rem 1rem',
              marginBottom: '1.5rem',
              fontSize: '0.875rem',
              color: '#475569'
            }}>
              Account: <strong>{user.email}</strong>
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: '1rem',
            flexDirection: 'column'
          }}>
            <button
              onClick={handleLogout}
              style={{
                padding: '0.875rem 1.5rem',
                background: '#1272B3',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <FiLogOut size={18} />
              Sign Out & Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Role-based access denied
  return (
    <div style={{ 
      padding: '4rem', 
      textAlign: 'center', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      background: '#f8fafc'
    }}>
      <div style={{
        width: 80,
        height: 80,
        background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 1.5rem'
      }}>
        <FiAlertTriangle size={40} color="#dc2626" />
      </div>

      <h1 style={{ fontSize: '2rem', color: '#dc2626', fontWeight: 700, marginBottom: '0.5rem' }}>
        Access Denied
      </h1>
      <p style={{ fontSize: '1rem', margin: '0.5rem 0 2rem', color: '#64748b', maxWidth: 400 }}>
        You don't have permission to view this page. This page may require a different role or permissions.
      </p>
      
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        <Link 
          to="/dashboard" 
          style={{ 
            padding: '0.75rem 1.5rem', 
            background: '#1272B3', 
            color: 'white', 
            borderRadius: '8px', 
            textDecoration: 'none',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <FiArrowLeft size={18} />
          Back to Dashboard
        </Link>
        
        <button
          onClick={handleLogout}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'transparent',
            color: '#64748b',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <FiLogOut size={16} />
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default Unauthorized;
